/**
 * CompactSqlPreview — collapsible live SQL preview of the pending statement.
 * The buffer is the source of truth, so this simply renders the current
 * statement text (already sliced from the live buffer by `buildCompactPreview`).
 *
 * Open by DEFAULT (prim #1 §2) so a user editing in the compact popover can
 * watch the SQL change without hunting for a collapsed footer. The open/closed
 * choice is remembered for the session via a transient `vqeStore.previewCollapsed`
 * flag (never persisted to disk), so a user who collapses it stays collapsed
 * until the app restarts. A native `<details>` keeps it keyboard-accessible
 * without an extra dependency.
 *
 * spec: docs/superpowers/specs/2026-06-14-vqe-ux-refinement-design.md § WS3
 *   (Footer — live SQL preview).
 */

import { ChevronRight } from 'lucide-react';

import { useAnalytics } from '../../../../../core/analytics/useAnalytics';
import { SqlPreviewCode } from '../../render/common/SqlPreviewCode';
import { useVqeStore } from '../../store/vqeStore';

export interface CompactSqlPreviewProps {
  readonly sql: string;
}

export function CompactSqlPreview({ sql }: CompactSqlPreviewProps) {
  const collapsed = useVqeStore((s) => s.previewCollapsed);
  const setPreviewCollapsed = useVqeStore((s) => s.setPreviewCollapsed);
  const { track } = useAnalytics();

  return (
    <details
      className="group rounded-md border border-border bg-muted/20"
      data-testid="vqe-compact-sql-preview"
      open={!collapsed}
      onToggle={(e) => {
        const nowOpen = e.currentTarget.open;
        // Ignore redundant toggles (React re-syncing `open` to the store value).
        if (nowOpen === !collapsed) return;
        setPreviewCollapsed(!nowOpen);
        track('feature:vqe_compact_preview_toggle', { enabled: nowOpen });
      }}
    >
      <summary
        className="flex cursor-pointer list-none items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium text-muted-foreground hover:text-foreground"
        data-testid="vqe-compact-sql-preview-toggle"
      >
        <ChevronRight className="h-3 w-3 transition-transform group-open:rotate-90" aria-hidden />
        SQL preview
      </summary>
      <pre
        className="max-h-32 overflow-auto whitespace-pre-wrap break-words border-t border-border px-2 py-1.5 font-mono text-[11px] text-foreground"
        data-testid="vqe-compact-sql-preview-text"
      >
        {sql ? <SqlPreviewCode sql={sql} /> : '—'}
      </pre>
    </details>
  );
}
